import type { PlacementFormPosition } from "@design-lab/shared/types";

/**
 * MN-8890 — industry-standard print placement dimensions for the Print ("Made") PDP,
 * keyed to garment size, for the three design-lab placements (front, frontLeftChest, back).
 *
 * This is AC #1 of MN-8890: "define and document a size-to-placement-dimension mapping for
 * front, back, and chest keyed to apparel size." It is the data source the (flag-gated,
 * follow-up) per-size auto-resize + Print PDP UI notice will read from.
 *
 * v1 mirrors the established, in-production Transfers table MAX_PRINT_SIZES_GROUPED
 * (frontend/components/jiffy/react-product-details-dtf/common/constants.ts) for the
 * matching locations (full-front, left-chest, full-back). It is intentionally a
 * Print-owned copy rather than an import so the two flows can diverge: MN-8890 requires
 * validating these values against a cited industry decoration-sizing reference and, if
 * Transfers diverges, correcting Print here and flagging Transfers in a separate ticket.
 * Until that validation lands (MN-8890 blocker B1) these values are the Jiffy standard.
 */

export type GarmentCategory = "adult" | "youth" | "toddler" | "infant";

export type StandardPlacement = {
  category: GarmentCategory;
  /** Apparel size range this row applies to within the category; omitted = whole category. */
  sizeRange?: string;
  widthInches: number;
  heightInches: number;
};

/**
 * Standard placement dimensions per design-lab placement. Within a category, rows are listed
 * most-specific first purely for human readability — `getPrintStandardPlacement` does a separate
 * exact `(category, sizeRange)` find then a catch-all `(category, no sizeRange)` find, so row
 * order does not affect correctness (an exact pair is unique).
 */
export const PRINT_STANDARD_PLACEMENT: Record<PlacementFormPosition, StandardPlacement[]> = {
  front: [
    { category: "adult", sizeRange: "XS - S", widthInches: 10, heightInches: 12 },
    { category: "adult", sizeRange: "M - L", widthInches: 11, heightInches: 14 },
    { category: "adult", sizeRange: "XL", widthInches: 12, heightInches: 14 },
    { category: "adult", sizeRange: "XXL+", widthInches: 13, heightInches: 15 },
    { category: "youth", sizeRange: "XS - S", widthInches: 7, heightInches: 7 },
    { category: "youth", sizeRange: "M - L", widthInches: 8, heightInches: 8 },
    { category: "youth", sizeRange: "XL", widthInches: 9, heightInches: 9 },
    { category: "toddler", sizeRange: "5T - 6T", widthInches: 6, heightInches: 6 },
    { category: "toddler", sizeRange: "2T - 4T", widthInches: 5, heightInches: 5 },
    { category: "infant", sizeRange: "6M - 24M", widthInches: 4, heightInches: 4 },
    { category: "infant", sizeRange: "Newborn", widthInches: 3, heightInches: 3 }
  ],
  frontLeftChest: [
    { category: "adult", sizeRange: "XS - M", widthInches: 3, heightInches: 3 },
    { category: "adult", sizeRange: "L+", widthInches: 3.5, heightInches: 3.5 },
    { category: "youth", sizeRange: "XS - M", widthInches: 2.75, heightInches: 2.75 },
    { category: "youth", sizeRange: "L+", widthInches: 3, heightInches: 3 },
    { category: "toddler", widthInches: 2, heightInches: 2 },
    { category: "infant", widthInches: 1.5, heightInches: 1.5 }
  ],
  back: [
    { category: "adult", sizeRange: "XS - M", widthInches: 10, heightInches: 13 },
    { category: "adult", sizeRange: "L+", widthInches: 12, heightInches: 15 },
    { category: "youth", sizeRange: "XS - S", widthInches: 9, heightInches: 12 },
    { category: "youth", sizeRange: "M+", widthInches: 10, heightInches: 13 },
    { category: "toddler", widthInches: 8, heightInches: 10 },
    { category: "infant", widthInches: 6, heightInches: 8 }
  ]
};

export type StandardPlacementDimensions = {
  widthInches: number;
  heightInches: number;
};

// Normalize a size range for comparison: strip all whitespace and lowercase so formatting drift
// from a future classifier (e.g. "M-L", "m - l ", "M - L") still resolves to the canonical row.
// Falsy -> undefined. Ranges stay distinct after stripping (e.g. "xs-s" vs "m-l" vs "xxl+").
function normalizeSizeRange(value?: string): string | undefined {
  const normalized = value?.replace(/\s+/g, "").toLowerCase();
  return normalized || undefined;
}

/**
 * Look up the standard print placement dimensions for a placement + garment classification.
 *
 * Matching is on (category, normalized sizeRange) — whitespace/case-insensitive on the range.
 * A row with no `sizeRange` is a category-wide catch-all, used when no `sizeRange` is supplied
 * or when no exact range row exists (toddler/infant). Returns `null` when nothing matches so
 * callers can fall back to the legacy surface-derived sizing rather than silently resize to a
 * wrong dimension.
 */
export function getPrintStandardPlacement(params: {
  placement: PlacementFormPosition;
  category: GarmentCategory;
  sizeRange?: string;
}): StandardPlacementDimensions | null {
  const { placement, category } = params;
  const rows = PRINT_STANDARD_PLACEMENT[placement];
  // Array.isArray (not a truthy check): an out-of-type placement key could resolve to an
  // inherited Object.prototype member (a truthy function), which would pass `!rows` and then
  // throw on `.find`. Guard against it and fail safe to null.
  if (!Array.isArray(rows)) return null;

  const sizeRange = normalizeSizeRange(params.sizeRange);

  // Exact (category, sizeRange) row wins.
  const exact = rows.find(
    row => row.category === category && normalizeSizeRange(row.sizeRange) === sizeRange
  );
  if (exact) return { widthInches: exact.widthInches, heightInches: exact.heightInches };

  // Otherwise fall back to a category-wide catch-all row (no sizeRange). This matters for
  // toddler/infant on back/frontLeftChest, which only have a whole-category standard: a future
  // garment-size classifier emitting a concrete range (e.g. "2T - 4T") must still resolve to it
  // rather than silently returning null and dropping back to legacy surface sizing.
  const catchAll = rows.find(row => row.category === category && !row.sizeRange);
  if (catchAll) return { widthInches: catchAll.widthInches, heightInches: catchAll.heightInches };

  return null;
}
